import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { ContextMenuItem } from '../../interfaces/context-menu/contextMenuItem';

/* *** See use case example at the bottom of the file *** */

@Component({
  selector: 'app-context-menu',
  templateUrl: './context-menu.component.html',
  styleUrls: ['./context-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule]
})
export class ContextMenuComponent implements AfterViewInit, OnChanges {
   /**
   * List of context menu items to be displayed.
   * Each item can have an action, an icon (with its own color), and a submenu.
   */
  @Input() items: ContextMenuItem[] = []; 

  /**
   * Absolute position where the context menu should appear.
   * @property x - Horizontal position (in pixels).
   * @property y - Vertical position (in pixels).
   */
  @Input() position = { x: 0, y: 0 };  

  /**
   * Parent container used to dynamically adjust the menu position.
   * Prevents the menu from overflowing outside its defined boundaries.
   */ 
  @Input() container!: DOMRect;

  /**
   * Event emitted when the context menu is closed (useful for updating variables in parent component)
   */
  @Output() onClose = new EventEmitter<void>(); 

  /**
   * Reference to the main menu element, used to determine its size and position.
   */
  @ViewChild('menu', { static: false }) menuElementRef!: ElementRef;

  /**
   * Reference to the submenu element, used to adjust its dynamic positioning.
   */
  @ViewChild('submenu', { static: false }) submenuElementRef!: ElementRef;

  /**
   * Adjusted position of the menu, calculated based on container boundaries.
   */
  adjustedPosition: { x: number; y: number } = { x: 0, y: 0 };

  /**
   * Width and height of the context menu (updated dynamically after rendering).
   */
  width: number = 120;
  height: number = 68; 

  /**
   * Width of the submenu, determined dynamically.
   */
  submenuWidth: number = 0;

  /**
   * Determines if the submenu should be displayed on the right side.
   */
  submenuRightSide: boolean = true;

  constructor(private cdr: ChangeDetectorRef) { }

  /**
   * After the menu is rendered, retrieves its dimensions and adjusts its position.
   */
  ngAfterViewInit(): void {
    if (this.menuElementRef?.nativeElement) {
      const rect = this.menuElementRef.nativeElement.getBoundingClientRect();
      this.width = rect.width;
      this.height = rect.height;
      this.adjustMenuPosition();
    }
  }

  /**
   * Adjusts the menu position when changes occur.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (this.adjustedPosition) {
      this.submenuRightSide = true;
      this.adjustMenuPosition();
    }
  }

  /**
   * Executes the action of a menu item if it's not disabled.
   * Automatically closes the menu after the action is performed.
   * @param item The selected menu item.
   */
  onItemClick(item: ContextMenuItem): void {
    if (item.disabled && item.disabled()) return; 
    item.action?.();           
    this.onClose.emit();     
  }

  /**
   * Prevents the browser's default context menu from appearing.
   * @param event Right-click event.
   */
  onContextMenu(event: MouseEvent) {
    event.preventDefault();
  }

  /**
   * Dynamically adjusts the position of the context menu to prevent overflows.
   */
  adjustMenuPosition(): void {
    if (!this.position) return;
    const topOffset = this.height / 2;
    const sideOffset = 15;

    let { x, y } = this.position;
    x -= (this.container.left - sideOffset);
    y -= (this.container.top - topOffset);

    let submenuWidth = this.submenuWidth ?? 190;
    // Adjustments to prevent overflow:
    // if in bottom-right corner
    if ((x + this.width + (this.submenuRightSide ? submenuWidth : 0) > this.container.width) && (y + this.height > this.container.height)) {
      y -= (this.height - topOffset / 2 + sideOffset);
      x -= (this.width + sideOffset * 2);
      this.submenuRightSide = false;
    } else if ((x < this.width) && (y + this.height > this.container.height)) { // if in bottom-left corner
      y -= (this.height - topOffset / 2 + sideOffset);
    } else if (x + this.width + (this.submenuRightSide ? submenuWidth : 0)> this.container.width) { // if on the right
      x -= (this.width + sideOffset * 2);
      this.submenuRightSide = false;
    } else if (y + this.height > this.container.height) { // if on the bottom
      y -= (this.height + sideOffset);
      x -= this.width / 2 + sideOffset;
    }

    this.adjustedPosition = { x, y };
    this.cdr.detectChanges();
  }

  onMouseOver(event: MouseEvent, submenu: any) {
    if (submenu && !this.submenuWidth) {
      if (this.submenuElementRef?.nativeElement) {
        const rect = this.submenuElementRef.nativeElement.getBoundingClientRect();
        this.submenuWidth = rect.width;
      }
    }
  }
}

/**
  // Example of use (Context menu for the zone editing marker in the Viewer Map)
  // Configuration in the parent component
  menuItems: ContextMenuItem[] = [
      { 
        label: 'form.delete', 
        icon: 'delete_forever', 
        iconColor: "rgba(183, 3, 3, 0.9)",
        action: () => this.deletePoint(), 
        disabled: () => this.zoneEditor?.points?.length <= 3
      },
      { 
        label: 'form.update', 
        icon: 'edit', 
        iconColor: 'blue',
        submenu: [
          { label: 'units.tracker_pos.from_tracker', icon: 'my_location', iconColor: 'rgba(70, 143, 60, 0.9)', action: () => this.openTrackerPosition() },
          { label: 'units.unit_pos.from_unit', icon: 'location_on', iconColor: 'rgba(70, 143, 60, 0.9)', action: () => this.useUnitPosition = true }
        ]
      }
    ];
  showContextMenu = false;
  menuPosition = { x: 0, y: 0 };
  mapRect : DOMRect;
  onMarkerRightClick: ({ point }) => {
    let { clientX, clientY } = this.getScreenCoords(point.lat, point.lng); 
    this.showContextMenu = true;
    this.mapRect = this.map.googleMap.getDiv().getBoundingClientRect();
    this.menuPosition = { x: clientX, y: clientY };
  }    

  // Template in the parent component
  <app-context-menu  
    *ngIf="showContextMenu"
    [items]="menuItems"
    [position]="menuPosition"
    [container]="mapRect"
    (onClose)="onContextMenuClose()">
  </app-context-menu>
*/